/**
 * The queue itself: what holds the writes, drains them in order, and tells
 * anybody who is listening how it is going.
 *
 * `src/lib/queue.ts` is every decision this makes, as pure functions; this is
 * the thing that carries them out. It is written against structural
 * interfaces — a `QueueStorage`, a `QueueSend` — for the same reason
 * `src/motion.ts` and `src/theme.ts` are: what is worth testing is the
 * sequencing (drain in order, stop on a refusal, back off on a failure,
 * resume when told to), and that is testable with an in-memory array standing
 * in for IndexedDB and a function standing in for `fetch`. `src/queue-document.ts`
 * is the one place that reaches for the real ones.
 */

import {
  afterAttempt,
  classify,
  classifyThrow,
  nextDue,
  summarise,
  type Attempt,
  type QueueSummary,
  type QueuedMethod,
  type QueuedWhat,
  type QueuedWrite,
} from './lib/queue'

export type QueueStorage = {
  all(): Promise<QueuedWrite[]>
  put(write: QueuedWrite): Promise<void>
  remove(id: string): Promise<void>
}

/**
 * Sends one write and reports back enough to classify the answer.
 *
 * It never throws for an HTTP-level refusal — that is `status` and `code` —
 * only for the write never having reached a server at all, which is what
 * `classifyThrow` is for.
 */
export type QueueSend = (write: QueuedWrite) => Promise<{ status: number; code?: string; body?: unknown }>

export type QueueController = {
  /** Queues a write and tries to drain immediately. */
  enqueue(write: {
    id: string
    key: string
    method: QueuedMethod
    path: string
    body: string | null
    what: QueuedWhat
  }): Promise<QueuedWrite>
  /** Attempts every due write, in order, until none is due or one is blocked. */
  drain(): Promise<void>
  /** A refused write's owner has decided to try again anyway. */
  retry(id: string): Promise<void>
  /** A refused write's owner has decided to give up on it. */
  discard(id: string): Promise<void>
  list(): QueuedWrite[]
  snapshot(): QueueSummary
  subscribe(listener: () => void): () => void
}

export function createQueueController(deps: {
  storage: QueueStorage
  send: QueueSend
  online(): boolean
  now?(): number
  /** A write landed, or was replayed. Told what was sent and what came back. */
  onSent?(write: QueuedWrite, body: unknown): void | Promise<void>
  /** Something in the queue changed shape — for a redraw or a re-schedule. */
  onChange?(): void
}): QueueController {
  const { storage, send, online, now = () => Date.now(), onSent, onChange } = deps

  let mirror: QueuedWrite[] = []
  let seqCounter = 0
  let draining = false
  let redrainAt: ReturnType<typeof setTimeout> | undefined
  const listeners = new Set<() => void>()

  const ready = storage.all().then((writes) => {
    mirror = writes
    seqCounter = writes.reduce((max, write) => Math.max(max, write.seq), 0)
  })
  // A queue that survived a reload has to resume by itself — nobody is going
  // to enqueue anything just to nudge it, and the writes already in it are
  // exactly the ones a restaurant-basement reconnection is waiting on.
  void ready.then(() => drain())

  function notify(): void {
    onChange?.()
    for (const listener of [...listeners]) listener()
  }

  function scheduleRedrain(at: number): void {
    clearTimeout(redrainAt)
    const wait = Math.max(0, at - now())
    redrainAt = setTimeout(() => {
      void drain()
    }, wait)
  }

  async function drain(): Promise<void> {
    await ready
    if (draining) return
    draining = true
    try {
      for (;;) {
        if (!online()) return
        const due = nextDue(mirror, now())
        if (!due) {
          // Nothing due right now — but something may be waiting on a timer,
          // and a write that just got a refusal must not silently stop the
          // clock on the ones that could still be reached some other way.
          const soonest = mirror
            .filter((w) => w.refusal === null)
            .map((w) => w.notBefore)
            .sort((a, b) => a - b)[0]
          if (soonest !== undefined) scheduleRedrain(soonest)
          return
        }

        let attempt: Attempt
        let body: unknown
        try {
          const result = await send(due)
          body = result.body
          attempt = classify(result.status, result.code)
        } catch (err) {
          attempt = classifyThrow(err)
        }

        if (attempt.outcome === 'sent') {
          await storage.remove(due.id)
          mirror = mirror.filter((w) => w.id !== due.id)
          notify()
          await onSent?.(due, body)
          continue
        }

        const next = afterAttempt(due, attempt, now())
        await storage.put(next)
        mirror = mirror.map((w) => (w.id === next.id ? next : w))
        notify()

        // `blocked` and `refused` both stop this pass: a blocked write is
        // waiting on somebody signing in, not on a clock, and a refused one
        // holds up everything queued behind it. `retry` keeps going in case a
        // later write in the queue is independent and due sooner — but the
        // head that just failed is not due again until its own backoff, so
        // the loop naturally moves on to whatever is next.
        if (attempt.outcome === 'blocked' || attempt.outcome === 'refused') return
      }
    } finally {
      draining = false
    }
  }

  return {
    async enqueue(write) {
      await ready
      const full: QueuedWrite = {
        ...write,
        seq: ++seqCounter,
        queuedAt: new Date(now()).toISOString().slice(0, 19).replace('T', ' '),
        attempts: 0,
        notBefore: 0,
        refusal: null,
      }
      await storage.put(full)
      mirror = [...mirror, full]
      notify()
      void drain()
      return full
    },

    drain,

    async retry(id) {
      await ready
      const write = mirror.find((w) => w.id === id)
      if (!write || write.refusal === null) return
      const next: QueuedWrite = { ...write, refusal: null, notBefore: now() }
      await storage.put(next)
      mirror = mirror.map((w) => (w.id === id ? next : w))
      notify()
      void drain()
    },

    async discard(id) {
      await ready
      await storage.remove(id)
      mirror = mirror.filter((w) => w.id !== id)
      notify()
    },

    list() {
      return mirror
    },

    snapshot() {
      return summarise(mirror)
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
